const _ = require("lodash");

const allBundleNames = [
	"office",
	"parent",
	"school",
	"trainer",
	"profile",
	"library",
	"beastbuilder",
	"theater",
	"lab",
];

//
// Some dependency injection machinery
//

let injectedLoadBundle;

/**
 * If you need to be able to load bundles at runtime, call this function at
 * startup with the export of loadBundle.js. Only the core bundle should do
 * this.
 *
 * If this function is not called, which is the case for things like the admin
 * bundle, using Bundles.js in a way that requires loading a bundle will throw
 * an error indicating such.
 *
 * To make it possible to require this file without loading the entirety of
 * BA's clientside JS, the function with the require.ensure calls bringing in
 * each bundle has been separated into its own file, loadBundle.js, which is
 * not required from here. Instead, the implementation must be manually wired
 * up in startup code by calling this inject function.
 *
 * Since Bundles.js exports widely useful functions like requireOpt, it is
 * difficult to break dep chains that lead to it. This is why we introduced
 * this system so that functionality like requireOpt is available to files
 * without that file bringing in everything.
 *
 * @param inj {function} The function to inject. Should be the result of
 *     requiring src/core/loadBundle.js.
 */
const injectLoadBundle = function(inj) {
	injectedLoadBundle = inj;
};

//
// Initialize state
//

// When a bundle is loaded, if its exports contain a function called
// initialize, that function will be called with arguments provided in this
// file's setInitializeParams. If the initialize function exists but
// setInitializeParams has not been called, the initialize call will wait for
// setInitializeParams to be called, and the bundle will not be considered
// loaded until then.

// The setExports function, which is never used in a typical browser page,
// bypasses this system. initialize must be called manually.

// What was passed to setInitializeParams. null if not yet called.
let initializeParams = null;
// Callbacks waiting for a setInitializeParams call. Each takes no arguments.
const pendingInitializeCbs = [];
// Bundles marked failed.
const failedBundles = {};

//
// State and functions for loading bundles and modules.
//

const allBundleData = {};
allBundleNames.forEach(bn => {
	allBundleData[bn] = {
		status: "none",
		cbList: [],
		exports: undefined,
		error: null,
	};
});

// For a bundle, call all callbacks in a list with the proper arguments.
const callCbs = function(bundleName, cbList) {
	const bd = allBundleData[bundleName];
	const arg1 = bd.status === "done" ? null : bd.error;
	const arg2 = bd.status === "done" ? bd.exports : null;
	cbList.forEach(cb => cb(arg1, arg2));
};

const finishLoadingBundle = function(bundleName, bundleExports) {
	const bd = allBundleData[bundleName];

	const afterInitialize = () => {
		const cbList = bd.cbList;
		if (typeof bundleExports.initialize === "function") {
			bundleExports.initialize(...initializeParams);
		}
		bd.cbList = [];
		bd.status = "done";
		bd.exports = bundleExports;
		callCbs(bundleName, cbList);
	};

	if (typeof bundleExports.initialize === "function" && !initializeParams) {
		pendingInitializeCbs.push(afterInitialize);
	} else {
		setTimeout(afterInitialize, 0);
	}
};

const startLoadingBundle = function(bundleName) {
	const bd = allBundleData[bundleName];
	bd.status = "pending";

	const onSuccess = bundleExports => {
		finishLoadingBundle(bundleName, bundleExports);
	};

	const onError = ex => {
		const cbList = bd.cbList;
		bd.cbList = [];
		bd.status = "error";
		bd.error = ex;
		callCbs(bundleName, cbList);
	};

	if (injectedLoadBundle) {
		injectedLoadBundle(bundleName, onSuccess, onError);
	} else {
		// This branch should never be reached. The most likely way is if things
		// like the admin pages end up using functionality that requires a loaded
		// bundle, which we don't want to allow.
		throw new Error(
			"loadBundle functionality was not injected into Bundles.js " +
				"but bundle " +
				bundleName +
				" tried to load"
		);
	}
};

//
// Exported functions
//

// Get the load status of the bundle. Returns one of the strings "none",
// "pending", "done", "error".
const getStatus = function(bundleName) {
	if (
		process.env.NODE_ENV !== "production" &&
		!_.includes(allBundleNames, bundleName)
	) {
		throw new Error("No bundle with name '" + bundleName + "'.");
	}
	return allBundleData[bundleName].status;
};

// Get the name of all loaded bundles
const getLoadedBundles = function() {
	const loadedBundles = [];
	allBundleNames.forEach(bn => {
		if (allBundleData[bn].status === "done") {
			loadedBundles.push(bn);
		}
	});
	return loadedBundles;
};

// Get the error loading a bundle, if any. Usually an exception object.
const getError = function(bundleName) {
	return allBundleData[bundleName].error;
};

// Manually set a bundle as loaded with the given exports. Normally not needed
// except in environments where bundle loading is not a thing. We assume the
// caller will handle calling initialize and ignore everything related to it.
const setExports = function(bundleName, bundleExports) {
	if (
		process.env.NODE_ENV !== "production" &&
		!_.includes(allBundleNames, bundleName)
	) {
		throw new Error("No bundle with name '" + bundleName + "'.");
	}

	const bd = allBundleData[bundleName];
	const cbList = bd.cbList;
	bd.cbList = [];
	bd.status = "done";
	bd.exports = bundleExports;
	callCbs(bundleName, cbList);
};

// If the bundle is loaded, this function returns its main file's exports.
// Else, it returns undefined. moduleName is for convenience: if provided, in
// the success case this will return bundleExports[moduleName].
const requireOpt = function(bundleName, moduleName) {
	// Development check that module name is valid.
	if (
		process.env.NODE_ENV !== "production" &&
		!_.includes(allBundleNames, bundleName)
	) {
		throw new Error("No bundle with name '" + bundleName + "'.");
	}
	const bundleExports = allBundleData[bundleName].exports;
	if (moduleName && bundleExports) {
		return bundleExports[moduleName];
	}
	return bundleExports;
};

// Load the bundle if it is not already loaded. Callback is called after the
// bundle loading is done with two parameters (err, exports). If the bundle
// loads successfully, err is null and exports is the main file's exports.
//
// You can only use this function when injectLoadBundle was called previously.
const load = function(bundleName, cb) {
	if (
		process.env.NODE_ENV !== "production" &&
		!_.includes(allBundleNames, bundleName)
	) {
		throw new Error("No bundle with name '" + bundleName + "'.");
	}
	if (cb !== undefined && typeof cb !== "function") {
		throw new Error("Bundle loading callback, if provided, must be a function");
	}
	const bd = allBundleData[bundleName];
	const status = bd.status;
	if (status === "none") {
		startLoadingBundle(bundleName);
	}

	if (!cb) {
		return;
	} else if (status === "none" || status === "pending") {
		bd.cbList.push(cb);
	} else if (status === "done" || status === "error") {
		setTimeout(() => callCbs(bundleName, [cb]), 0);
	}
};

// Identical to the load method, except that if the bundle status is error,
// this will reset the status and start a new load.
const retry = function(bundleName, cb) {
	const status = getStatus(bundleName);
	if (bundleName && status === "error") {
		allBundleData[bundleName].status = "none";
	}
	return load(bundleName, cb);
};

// Pass all the parameters that loaded initialize functions should be called
// with. See comments above near initialize state for more info.
const setInitializeParams = function(...params) {
	initializeParams = params;
	while (pendingInitializeCbs.length) {
		pendingInitializeCbs.shift()();
	}
};

// Call f if this is the first call to this function for a given bundle name.
// Else do nothing. This is called by other code when something has gone wrong
// with a bundle and it wants to handle the issue with ErrorUtil or otherwise.
// We use this to avoid being noisy.
const markFailed = function(bundleName, f) {
	if (failedBundles[bundleName]) return;
	failedBundles[bundleName] = true;
	f();
};

module.exports = {
	injectLoadBundle,
	getStatus,
	getLoadedBundles,
	getError,
	setExports,
	requireOpt,
	load,
	retry,
	setInitializeParams,
	markFailed,
};
